import { useCallback, useState } from 'react';
import {
  getSurveySampleByIdApi,
  getSurveySampleDimensionsByIdApi,
  updateSurveyDimensionByIdApi,
  updateSurveySampleByIdApi,
} from '../../../api/survey.sample';
import { clearSurveyCacheApi } from '../../../api/surveys';

// Owns the sample + dimensions of the attribute filter being edited. Same
// load/save contract as useQuestionEditor, minus the chart store mirroring
// (filters have no chart preview) and the location-filter special case (the
// filter category is locked to ATTRIBUTE for now).
const useFilterEditor = (jwt) => {
  const [sample, setSample] = useState();
  const [dimensions, setDimensions] = useState();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadFilter = useCallback(
    async (sampleId) => {
      setLoading(true);
      setLoadError(null);
      // Clear the previous filter so the form never shows stale data while
      // the new one loads.
      setSample(undefined);
      setDimensions(undefined);
      try {
        const [sampleData, dimensionsData] = await Promise.all([
          getSurveySampleByIdApi(sampleId, jwt),
          getSurveySampleDimensionsByIdApi(sampleId, jwt),
        ]);
        if (!sampleData || !dimensionsData || dimensionsData.length === 0) {
          throw new Error(`No sample or dimensions found for filter sample ${sampleId}`);
        }
        setSample(sampleData);
        setDimensions(dimensionsData);
        return { sample: sampleData, dimensions: dimensionsData };
      } catch (e) {
        console.error(e);
        setLoadError(e);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [jwt]
  );

  const clearLoadError = useCallback(() => setLoadError(null), []);

  const saveFilter = useCallback(
    async ({ samplePatch, updatedDimensions, surveyId }) => {
      setSaving(true);
      try {
        await Promise.all([
          updateSurveySampleByIdApi(samplePatch.Id, samplePatch, jwt),
          ...updatedDimensions.map((dimension) =>
            updateSurveyDimensionByIdApi(dimension.Id, dimension, jwt)
          ),
        ]);
        await clearSurveyCacheApi(surveyId, jwt);
        // Keep local state in sync with what was just saved so the form never
        // falls back to pre-save values while the filter list refetches.
        setSample((prev) =>
          prev ? { ...prev, Data: { ...prev.Data, ...samplePatch.Data } } : prev
        );
        setDimensions(updatedDimensions);
      } finally {
        setSaving(false);
      }
    },
    [jwt]
  );

  return {
    sample,
    dimensions,
    loading,
    saving,
    loadError,
    clearLoadError,
    loadFilter,
    saveFilter,
  };
};

export default useFilterEditor;
